import { useFocusRing } from '@react-aria/focus'
import { ariaHideOutside, useOverlayTrigger } from '@react-aria/overlays'
import { mergeProps, mergeRefs } from '@react-aria/utils'
import { useOverlayTriggerState, type OverlayTriggerState } from '@react-stately/overlays'
import type { OverlayTriggerProps } from '@react-types/overlays'
import type { PressEvent } from '@react-types/shared'
import clsx from 'clsx'
import type React from 'react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react'
import { View } from 'react-native'
import type { ViewStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes'

import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { useDOMRef } from '../../general/utils/useDOMref'
import type { TAriaButtonProps } from '../button/useAriaButton'
import { useReactAriaPopover, type TReactAriaPopoverProps } from './useAriaPopover'
import { getArrowPlacement } from './utils/getArrowPlacement'
import { getShouldUseAxisPlacement } from './utils/getShouldAxisPlacement'
import { popover, type TPopoverSlots, type TPopoverVariantProps } from './variants/Popover'

type TProps = HTMLUiProps & {
  /**
   * Ref to the DOM node.
   */
  ref?: React.ForwardedRef<'div'>
  /**
   * The controlled state of the popover.
   */
  state?: OverlayTriggerState
  /**
   * The ref for the element which the overlay positions itself with respect to.
   */
  triggerRef?: RefObject<HTMLElement>
  /**
   * Whether the scroll event should be blocked when the overlay is open.
   * @default true
   */
  shouldBlockScroll?: boolean
  /**
   * Type of overlay that is opened by the trigger.
   */
  triggerType?: 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid'
  /**
   * The container element in which the overlay portal will be placed.
   * @default document.body
   */
  portalContainer?: Element
  /**
   * Classname or List of classes to change the classNames of the element.
   * if `className` is passed, it will be added to the base slot.
   *
   * @example
   * ```ts
   * <Popover classNames={{
   *    base:"base-classes",
   *    content: "content-classes",
   *    trigger: "trigger-classes",
   *    backdrop: "backdrop-classes",
   * }} />
   * ```
   */
  classNames?: SlotsToClasses<TPopoverSlots>
  /**
   *  Callback fired when the popover is closed.
   */
  onClose?: VoidFunction
}

export type TUsePopoverProps = TProps &
  Omit<TReactAriaPopoverProps, 'triggerRef' | 'popoverRef'> &
  OverlayTriggerProps &
  TPopoverVariantProps & {
    children?: ReactNode
    as?: React.ElementType
    className?: string
    style?: ViewStyle
  }

export function usePopover(originalProps: TUsePopoverProps) {
  const [props, variantProps] = mapPropsVariants(originalProps, popover.variantKeys)

  const {
    as,
    children,
    ref,
    state: stateProp,
    triggerRef: triggerRefProp,
    scrollRef,
    defaultOpen,
    onOpenChange,
    isOpen: isOpenProp,
    isNonModal = true,
    shouldFlip = true,
    containerPadding = 12,
    shouldBlockScroll = false,
    isDismissable = true,
    shouldCloseOnBlur,
    portalContainer,
    updatePositionDeps,
    placement: placementProp = 'top',
    triggerType = 'dialog',
    showArrow = false,
    offset = 7,
    crossOffset = 0,
    boundaryElement,
    isKeyboardDismissDisabled,
    shouldCloseOnInteractOutside,
    className,
    classNames,
    onClose,
    ...otherProps
  } = props

  const Component = as || View

  const domTriggerRef = useRef<HTMLElement>(null)
  const wasTriggerPressedRef = useRef(false)
  const domRef = useDOMRef(ref)

  const triggerRef = triggerRefProp || domTriggerRef

  const isAnimationDisabled = originalProps.disableAnimation ?? false

  const innerState = useOverlayTriggerState({
    isOpen: isOpenProp,
    defaultOpen,
    onOpenChange: (isOpen) => {
      onOpenChange?.(isOpen)
      if (!isOpen) {
        onClose?.()
      }
    },
  })

  const state = stateProp || innerState

  const {
    popoverProps,
    underlayProps,
    placement: ariaPlacement,
  } = useReactAriaPopover(
    {
      triggerRef,
      isNonModal,
      // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- ref bridge
      popoverRef: domRef as RefObject<HTMLElement>,
      placement: placementProp,
      offset,
      scrollRef,
      isDismissable,
      shouldCloseOnBlur,
      boundaryElement,
      crossOffset,
      shouldFlip,
      containerPadding,
      updatePositionDeps,
      isKeyboardDismissDisabled,
      shouldCloseOnInteractOutside,
    },
    state,
  )

  const { triggerProps } = useOverlayTrigger({ type: triggerType }, state, triggerRef)

  const { isFocusVisible, isFocused, focusProps } = useFocusRing()

  const slots = popover({
    ...variantProps,
  })

  const baseStyles = clsx(classNames?.base, className)

  const getPopoverProps: PropGetter<HTMLAttributes<HTMLDivElement>, HTMLDivElement> = (p = {}) => ({
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- ref bridge
    ref: domRef as Ref<HTMLDivElement>,
    ...mergeProps(popoverProps, otherProps, p),
    style: mergeProps(popoverProps.style, otherProps.style, p.style),
  })

  const getDialogProps: PropGetter<HTMLAttributes<HTMLDivElement>> = (overrides = {}) => ({
    'data-slot': 'base',
    'data-open': state.isOpen,
    'data-focus': isFocused,
    'data-arrow': showArrow,
    'data-focus-visible': isFocusVisible,
    'data-placement': getArrowPlacement(ariaPlacement, placementProp),
    ...mergeProps(focusProps, overrides),
    className: slots.base({ class: clsx(baseStyles) }),
    style: {
      // this prevent the dialog to have a default outline
      outline: 'none',
    },
  })

  const getContentProps = useCallback<PropGetter<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>(
    (p = {}) => ({
      'data-slot': 'content',
      'data-open': state.isOpen,
      'data-arrow': showArrow,
      'data-placement': getArrowPlacement(ariaPlacement, placementProp),
      className: slots.content({ class: clsx(classNames?.content, p.className) }),
    }),
    [slots, state.isOpen, showArrow, ariaPlacement, placementProp, classNames],
  )

  const placement = useMemo(
    () => (getShouldUseAxisPlacement(ariaPlacement, placementProp) ? ariaPlacement : placementProp),
    [ariaPlacement, placementProp],
  )

  const onPress = (e: PressEvent) => {
    let pressTimer: ReturnType<typeof setTimeout>

    // Artificial delay to prevent the underlay to be triggered immediately after the onPress
    // this only happens when the backdrop is blur or opaque & pointerType === "touch"
    // TODO: find a better way to handle this
    if (e.pointerType === 'touch' && (originalProps?.backdrop === 'blur' || originalProps?.backdrop === 'opaque')) {
      pressTimer = setTimeout(() => {
        wasTriggerPressedRef.current = true
      }, 100)
    } else {
      wasTriggerPressedRef.current = true
    }

    triggerProps.onPress?.(e)

    return () => {
      clearTimeout(pressTimer)
    }
  }

  const getTriggerProps: PropGetter<TAriaButtonProps & HTMLAttributes<HTMLElement>, HTMLElement> = (p = {}, _ref) => ({
    'data-slot': 'trigger',
    'aria-haspopup': 'dialog',
    ...mergeProps(triggerProps, p),
    onPress,
    className: slots.trigger({ class: clsx(classNames?.trigger, p.className) }),
    ref: mergeRefs(_ref || null, triggerRef),
  })

  const getBackdropProps = useCallback<PropGetter<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>(
    (p = {}) => ({
      'data-slot': 'backdrop',
      className: slots.backdrop({ class: classNames?.backdrop }),
      onClick: (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (!wasTriggerPressedRef.current) return

        state.close()
        wasTriggerPressedRef.current = false
      },
      ...underlayProps,
      ...p,
    }),
    [slots, state, classNames, underlayProps],
  )

  useEffect(() => {
    if (state.isOpen && domRef?.current) {
      return ariaHideOutside([domRef?.current])
    }
  }, [state.isOpen, domRef])

  return {
    state,
    Component,
    children,
    classNames,
    showArrow,
    triggerRef,
    placement,
    isNonModal,
    popoverRef: domRef,
    portalContainer,
    isOpen: state.isOpen,
    onClose: state.close,
    disableAnimation: isAnimationDisabled,
    shouldBlockScroll,
    backdrop: originalProps.backdrop ?? 'transparent',
    getBackdropProps,
    getPopoverProps,
    getTriggerProps,
    getDialogProps,
    getContentProps,
  }
}

export type TUsePopoverReturn = ReturnType<typeof usePopover>
